import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Material
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

// Components
import Spinner from '../../../../../components/spinner';
import Textfield from '../../../../../components/inputs/input';

// Services 
import CashRegisterService from '../../../../../services/cashRegisterService';
import Dropdown from '../../../../../components/inputs/dropdown';
import useCashRegisters from '../../../../../hooks/cashRegisters/useCashRegisters';

// Hooks
import useNotify from '../../../../../hooks/notify';
import useUser from '../../../../../hooks/user/useUser';
import useTotals from '../../../../../hooks/cashRegisters/useTotals';
import useCashRegisterMovementReasons from '../../../../../hooks/cashRegisterMovementReasons/useCashRegisterMovementReasons';

// Models
import { cashRegisterMovementModel, movementTypes, cashTypes, subTypes } from '../../../../../models/CashRegisterMovementEntity';
import { utils } from '../../../../../utils/utils';

const CreateIngressModal = ({ onClose, onSave, open }) => {
    const { email, pv, business: { defaultCurrency }, business } = useUser();

    const { cashRegisterMovementReasons } = useCashRegisterMovementReasons({ 
        type: movementTypes.ingress,
        subType: subTypes.Adjustment
    });
    const [movement, setMovement] = useState({
        ...cashRegisterMovementModel,
        type: movementTypes.ingress,
        userEmail: email,
        cashType: cashTypes.cash,
        billingCode: pv.billingCode,
        subType: subTypes.Adjustment,
        currency: defaultCurrency
    });

    const [cashRegister, setCashRegister] = useState({})
    const [loading, setLoading] = useState(false);
    const { cashRegisters } = useCashRegisters({ isImprest: false });
    const { totals, getTotals, loading: loadingTotals } = useTotals();
    const { Notify, variants } = useNotify();

    useEffect(() => {
        if (cashRegisters && cashRegisters.length > 0) {
            setCashRegister(cashRegisters[0])
        }
    }, [cashRegisters])

    useEffect(() => {
        if (cashRegister && cashRegister.id) {
            getTotals(cashRegister.id);
            setMovement(state => ({ ...state, cashRegisterId: cashRegister.id }));
        }
    }, [cashRegister, getTotals]);

    const handleCancel = () => {
        onClose();
    }

    const handleCashRegisterChange = (event) =>
        setCashRegister(event.target.value)

    const handleValueChange = (event) => {
        setMovement({ ...movement, [event.target.name]: event.target.value });
    }

    const handleNumericValueChange = (event) => {
        setMovement({
            ...movement,
            [event.target.name]: event.target.value ? Number.parseFloat(event.target.value) : null,
        });
    }

    const handleSave = async (movement) => {
        try {
            setLoading(true);
            await CashRegisterService.CreateMovement(movement)
                .then(() => onSave())
                .catch((err) => Notify(err.message, variants.ERROR));
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    const GetCashTotal = () =>
        totals?.[`Cash${movement.currency}`]?.total || 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth='sm'
        >
            <DialogTitle id="alert-dialog-title">
                Ingreso de Caja
            </DialogTitle>
            <DialogContent dividers>
                {
                    loading &&
                    <Spinner lockScreen message='Creando Movimiento' />
                }
                <Grid container direction='column'>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Dropdown
                                required
                                fullWidth
                                name='cashRegister'
                                label='Caja'
                                onChange={handleCashRegisterChange}
                                size='small'
                                getOptionLabel={(option) => `${option?.name}`}
                                getOptionValue={(option) => option}
                                values={cashRegisters?.filter(cr => !cr.isImprest)}
                                value={cashRegister}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Dropdown
                                required
                                fullWidth
                                name='reasonId'
                                label='Seleccionar Motivo'
                                onChange={handleValueChange}
                                getOptionValue={(option) => option?.id}
                                getOptionLabel={(option) => option?.description}
                                values={cashRegisterMovementReasons}
                                value={movement.reasonId}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Dropdown
                            fullWidth
                            disabled={business.currencies.length <= 1}
                            values={business.currencies}
                            size="small"
                            name="currency"
                            label="Moneda"
                            onChange={handleValueChange}
                            getOptionLabel={(option) => option}
                            getOptionValue={(option) => option}
                            value={movement?.currency || ''}
                            />
                        </Grid>
                        <Grid size={9}>
                            <Textfield
                                required
                                fullWidth
                                size='small'
                                name='amount'
                                label='Monto a ingresar'
                                type='number'
                                onChange={handleNumericValueChange}
                                value={movement.amount}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Textfield
                                required
                                fullWidth
                                multiline
                                rows={5}
                                size="small"
                                name="observation"
                                label="Observación"
                                onChange={handleValueChange}
                                value={movement.observation || ""}
                            />
                        </Grid>
                        <Grid container>
                            <Grid size={6}>
                                <Typography color='primary' variant='body1'>
                                    Importe en efectivo:
                                </Typography>
                            </Grid>
                            <Grid size={6}>
                                {
                                    loadingTotals
                                        ? <CircularProgress size={20} style={{ marginLeft: '5px' }} />
                                        :                                        <Typography variant='body1' style={{ marginLeft: '5px' }}>
                                            {utils.formatMoney(GetCashTotal())}
                                        </Typography>
                                }
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid size={6}>
                                <Typography color='primary' variant='body1'>
                                    Nuevo saldo:
                                </Typography>
                            </Grid>
                            <Grid size={6}>
                                {
                                    loadingTotals
                                        ? <CircularProgress size={20} style={{ marginLeft: '5px' }} />
                                        :                                        <Typography variant='body1' style={{ marginLeft: '5px' }}>
                                            {utils.formatMoney(GetCashTotal() + (movement?.amount || 0))}
                                        </Typography>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleCancel}
                    color="primary"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={() => handleSave(movement)}
                    color="primary"
                    variant='outlined'
                    autoFocus
                    disabled={movement.amount <= 0 || !movement.cashRegisterId || !movement.userEmail || !movement.observation || !movement.reasonId}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

CreateIngressModal.defaultProps = {
    onSave: () => { },
    onClose: () => { },
}

CreateIngressModal.prototype = {
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    cashRegister: PropTypes.object
}

export default CreateIngressModal;